import type {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  ElementType,
  ReactNode,
} from "react";
import type { IconComponent } from "./icons";

export type ButtonVariant =
  | "ink"
  | "primary"
  | "accent"
  | "paper"
  | "line"
  | "ghost"
  | "quiet";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

type AnyButtonAttrs = ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement>;

export interface ButtonProps extends Omit<AnyButtonAttrs, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconComponent | null;
  iconRight?: IconComponent | null;
  className?: string;
  as?: ElementType;
  children?: ReactNode;
  type?: "button" | "submit" | "reset";
}

const SIZE_CLASS: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-[12px] gap-1.5",
  sm: "h-8 px-3 text-[12.5px] gap-1.5",
  md: "h-10 px-4 text-[13px] gap-2",
  lg: "h-12 px-6 text-[14px] gap-2.5",
};

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  ink: "bg-[var(--primary)] text-[var(--neutral-content)] hover:bg-[var(--primary-hover)]",
  primary:
    "bg-[var(--primary)] text-[var(--neutral-content)] hover:bg-[var(--primary-hover)]",
  accent:
    "bg-[var(--accent)] text-[var(--secondary)] hover:bg-[var(--accent-strong)] hover:text-[var(--neutral-content)]",
  paper:
    "bg-[var(--paper)] text-[var(--primary)] border border-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--neutral-content)]",
  line:
    "bg-transparent text-[var(--base-content)] border border-[var(--rule)] hover:border-[var(--primary)] hover:text-[var(--primary)]",
  ghost: "bg-transparent text-[var(--base-content)] hover:bg-[var(--paper-3)]",
  quiet: "bg-transparent text-[var(--neutral)] hover:text-[var(--primary)]",
};

const ICON_SIZE: Record<ButtonSize, number> = { xs: 13, sm: 14, md: 15, lg: 16 };

export function Button({
  variant = "ink",
  size = "md",
  icon: I,
  iconRight: IR,
  className = "",
  as: As = "button",
  children,
  ...rest
}: ButtonProps) {
  const Component = As as ElementType;
  return (
    <Component
      className={`inline-flex items-center justify-center whitespace-nowrap font-medium uppercase tracking-[0.06em] transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed ${SIZE_CLASS[size]} ${VARIANT_CLASS[variant]} ${className}`}
      style={{ fontFamily: "var(--sans)" }}
      {...rest}
    >
      {I && <I size={ICON_SIZE[size]} strokeWidth={1.75} />}
      <span>{children}</span>
      {IR && <IR size={ICON_SIZE[size]} strokeWidth={1.75} />}
    </Component>
  );
}
